import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";

import {
  fetchVendors,
  approveVendor,
  clearVendorState,
} from "../redux/slices/vendorSlice";

const AllVendorsList = () => {
  
  const dispatch = useDispatch();

  const { vendors, loading, successMessage, error, pagination } = useSelector(
    (state) => state.vendor
  );


  // -------------------------------
  // 🔍 Search + Tabs State
  // -------------------------------
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // all / verified / unverified


  // Fetch vendors
  useEffect(() => {
    dispatch(fetchVendors({ page: 1, limit: 20 }));
  }, [dispatch]);


  // Alerts
  useEffect(() => {
    if (successMessage) {
      Swal.fire("Success", successMessage, "success");
      dispatch(clearVendorState());
    }
    if (error) {
      Swal.fire("Error", error, "error");
      dispatch(clearVendorState());
    }
  }, [successMessage, error, dispatch]);

  // Approve Vendor
  const handleApprove = (id) => {
    dispatch(approveVendor(id));
  };

  // -------------------------------
  // 🔎 Filter Logic (Frontend)
  // -------------------------------
  const filteredVendors = vendors?.filter((vendor) => {
    const search = searchText.toLowerCase();

    const matchesSearch =
      vendor.shopName?.toLowerCase().includes(search) ||
      vendor.ownerName?.toLowerCase().includes(search) ||
      vendor.mobile?.includes(search);

    const matchesStatus =
      activeTab === "all"
        ? true
        : activeTab === "verified"
          ? vendor.isApproved === true
          : vendor.isApproved === false;

    return matchesSearch && matchesStatus;
  });


  return (
    <div className="container mt-4">
      <div className="card">
        <div className="card-header text-center">
          <h5 className="my-2">All Vendors</h5>
        </div>

        {/* Search */}
        <div className="p-3 d-flex justify-content-between align-items-center">
          <input
            type="text"
            className="form-control w-50"
            placeholder="Search by name or phone number..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        {/* Tabs */}
        <ul className="nav border-none nav-tabs px-3 my-3 d-flex gap-3 mb-4" style={{ border: 0 }}>
          {["all", "verified", "unverified"].map((tab) => (
            <li className="nav-item" key={tab}>
              <button
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                  backgroundColor: activeTab === tab ? "#f41b3b" : "#efefef",
                  color: activeTab === tab ? "#fff" : "#000",
                }}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            </li>
          ))}
        </ul>

        {loading && <p className="text-center">Loading...</p>}

        {/* Vendors Table */}
        <div className="table-responsive mt-3">
          <table className="table table-sm table-sm table-bordered table-striped align-middle">
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>Image</th>
                <th>Owner Name</th>
                <th>Shop Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Location</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVendors?.length > 0 ? (
                filteredVendors.map((vendor, index) => (
                  <tr className="text-small" key={vendor._id}>
                    <td>{index + 1}</td>
                    <td>
                      <img src={vendor.shopImage || "https://placehold.co/600x400"} width={60} height={60} alt={vendor.shopName} />
                    </td>
                    <td>{vendor.ownerName}</td>
                    <td>{vendor.shopName}</td>
                    <td>{vendor.phoneNumber}</td>
                    <td>{vendor.email || "N/A"}</td>
                    <td>
                      {vendor.address || "N/A"}, {vendor.city || "N/A"}, {vendor.state || "N/A"}
                    </td>
                    <td>
                      {vendor.isApproved ? (
                        <span className="badge bg-success">Approved</span>
                      ) : (
                        <span className="badge bg-warning text-dark">Pending</span>
                      )}
                    </td>
                    <td className="text-center">
                      <Link
                        to={`/vendor-profile/${vendor._id}`}
                        className="btn btn-sm btn-primary me-1"
                      >
                        View
                      </Link>
                      {!vendor.isApproved && (
                        <button
                          className="btn btn-sm btn-success me-1"
                          onClick={() => handleApprove(vendor._id)}
                        >
                          Approve
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">
                    No vendors found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && (
          <div className="d-flex justify-content-center mt-3 gap-2">
            <button
              disabled={pagination.page === 1}
              className="btn btn-secondary"
              onClick={() =>
                dispatch(fetchVendors({ page: pagination.page - 1, limit: 20 }))
              }
            >
              Previous
            </button>
            <button
              disabled={pagination.page === pagination.totalPages}
              className="btn btn-secondary"
              onClick={() =>
                dispatch(fetchVendors({ page: pagination.page + 1, limit: 20 }))
              }
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllVendorsList;
